import { useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { WHATSAPP_NUMBER_E164, shortBookingReference } from "@majourneys/shared";
import {
  BEVEL_CHIP_ON,
  BEVEL_INSET_WHITE,
  BEVEL_NAVY_FACE,
  BEVEL_WHITE_FACE,
  CONTROL_EDGE,
} from "@majourneys/shared/ui";
import { bookingErrorText } from "../lib/booking-error";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { bookingsApi, bookingKeys } from "../api/bookings.api";
import { PW } from "../internal-ui/pewter-shell";
import {
  pewterCloseButton,
  pewterDialog,
  pewterErrorNote,
  pewterInput,
  pewterScrim,
} from "../internal-ui/pewter-modal";

import { Icon } from "@majourneys/icons";

/**
 * Why a guest is cancelling, as one tap each (owner, 2026-10-25, idea 86 in
 * the suggestions lab). The label is translated; what reaches the API is the
 * English word, because our team reads it in the admin.
 */
const REASONS = [
  { key: "plans", en: "Plans changed" },
  { key: "weather", en: "Weather" },
  { key: "found_else", en: "Found something else" },
  { key: "mistake", en: "Booked by mistake" },
  { key: "other", en: "Other" },
] as const;

type ReasonKey = (typeof REASONS)[number]["key"];

/*
 * The chips' faces: the soft bevel, and the chosen one navy with no light along
 * its top (owner, 2026-10-25, variant A). Applied as styles rather than the
 * pewter chip class because this modal is also served on the city sites,
 * which do not load that class.
 */
const CHIP_REST: CSSProperties = {
  background: BEVEL_WHITE_FACE,
  color: PW.navy,
  boxShadow: `${BEVEL_INSET_WHITE}, 0 0 0 1px ${CONTROL_EDGE}`,
};
const CHIP_ON: CSSProperties = {
  background: BEVEL_NAVY_FACE,
  color: "#fff",
  boxShadow: BEVEL_CHIP_ON,
};

interface CancelBookingModalProps {
  bookingReference: string;
  activityTitle: string;
  /** The booked day, `YYYY-MM-DD`, shown beside the title. */
  date?: string;
  /** A desert camp, paid online from a link our team emails, not on arrival. */
  payByLink?: boolean;
  /**
   * "Change the date instead", when the guest can change it themselves (the
   * booking is still pending): the caller closes this and opens Modify.
   */
  onChangeDate?: () => void;
  /**
   * "Change the date instead" as a WhatsApp message, for a booking the guest
   * cannot change themselves (confirmed: a provider is holding the slot).
   */
  changeDateByWhatsApp?: boolean;
  onClose: () => void;
  onCancelled?: () => void;
}

/**
 * Cancel, or change the date instead (owner, 2026-10-25, idea 86).
 *
 * Many cancellations are really "a different day", so the new date comes
 * first and is the loud choice; cancelling is a quiet red line at the bottom.
 * The reason is optional chips rather than a blank box. The old "I understand
 * this cannot be undone" checkbox is gone: the red button already says what
 * it does, and nothing was paid online, so a cancel costs the guest nothing.
 */
export function CancelBookingModal({
  bookingReference,
  activityTitle,
  date,
  payByLink = false,
  onChangeDate,
  changeDateByWhatsApp = false,
  onClose,
  onCancelled,
}: CancelBookingModalProps) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [reason, setReason] = useState<ReasonKey | null>(null);
  const [note, setNote] = useState("");

  const apiReason = (() => {
    if (!reason) return undefined;
    const en = REASONS.find((r) => r.key === reason)!.en;
    const extra = reason === "other" ? note.trim() : "";
    return extra ? `${en}: ${extra}` : en;
  })();

  const mutation = useMutation({
    mutationFn: () => bookingsApi.cancel(bookingReference, apiReason),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: bookingKeys.detail(bookingReference, i18n.language) });
      onCancelled?.();
      onClose();
    },
  });

  // Mapped by the API's code to copy in the guest's language: never the
  // API's own English, nor a 422's validation JSON or "Failed to fetch".
  const errorText = mutation.error ? bookingErrorText(mutation.error, t as never, "cancel") : null;

  // The booked day, built from its parts: parsed as UTC and shown in a
  // western timezone, a plain calendar date slides back a day.
  const dateLabel = (() => {
    if (!date) return null;
    const [y, m, d] = date.split("-").map(Number);
    if (!y || !m || !d) return null;
    // Day before month in English too ("Wed 28 Oct"), the way Morocco and
    // Europe write it; plain "en" would print the US order.
    const locale = i18n.language === "en" ? "en-GB" : i18n.language;
    return new Intl.DateTimeFormat(locale, { weekday: "short", day: "numeric", month: "short" }).format(
      new Date(y, m - 1, d),
    );
  })();

  const byWhatsApp = !onChangeDate && changeDateByWhatsApp;
  const changeDateHref = byWhatsApp
    ? `https://wa.me/${WHATSAPP_NUMBER_E164.replace(/^\+/, "")}?text=${encodeURIComponent(
        t("activity.change_date_whatsapp", {
          reference: shortBookingReference(bookingReference) || bookingReference,
          title: activityTitle,
        }),
      )}`
    : null;

  const changeDateInner = (
    <>
      <span
        className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-white"
        style={{ background: BEVEL_NAVY_FACE }}
        aria-hidden
      >
        <Icon name="ri-calendar-schedule-line" className="text-[17px]" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-sans text-[13.5px] font-bold text-[#110E39]">
          {t("activity.cancel_change_date", { defaultValue: "Change the date instead" })}
        </span>
        <span className="block font-sans text-[12px] font-medium text-[#5a5d77]">
          {byWhatsApp
            ? t("activity.cancel_change_date_whatsapp_sub", { defaultValue: "Message us on WhatsApp with the new day" })
            : t("activity.cancel_change_date_sub", { defaultValue: "Keep your booking, pick another day" })}
        </span>
      </span>
      <Icon
        name={byWhatsApp ? "ri-whatsapp-line" : "ri-arrow-right-s-line"}
        className={`text-[18px] text-[#110E39] ${byWhatsApp ? "" : "rtl:-scale-x-100"}`}
        aria-hidden
      />
    </>
  );
  const changeDateClass = "mt-4 flex w-full items-center gap-3 rounded-[16px] bg-white p-3 text-start";
  const changeDateStyle: CSSProperties = { boxShadow: `0 0 0 1.5px ${PW.navy}` };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("activity.cancel_title_question", { defaultValue: "Cancel your booking?" })}
      className={`fixed inset-0 z-[70] flex items-end justify-center p-3 pb-[max(12px,env(safe-area-inset-bottom))] sm:items-center sm:p-4 ${pewterScrim}`}
      onClick={onClose}
    >
      {/* Pewter, like the sheet it opens from (owner, 2026-09-10). Red is kept
          for the one control that cancels, not spread across the dialog. */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="max-h-[calc(86dvh_/_var(--mj-ui-zoom,1))] w-full overflow-y-auto rounded-[26px] p-4 pb-3 sm:max-w-md"
        style={pewterDialog()}
      >
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <h2 className="font-heading text-[18px] font-bold text-[#110E39]">
              {t("activity.cancel_title_question", { defaultValue: "Cancel your booking?" })}
            </h2>
            <p className="mt-0.5 font-sans text-[12.5px] font-medium text-[#5a5d77]">
              {activityTitle}
              {dateLabel ? ` · ${dateLabel}` : ""}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("common.close", { defaultValue: "Close" })}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
            style={pewterCloseButton}
          >
            <Icon name="ri-close-line" className="text-[16px] text-[#110E39]" aria-hidden />
          </button>
        </div>

        {onChangeDate ? (
          <button type="button" onClick={onChangeDate} className={changeDateClass} style={changeDateStyle}>
            {changeDateInner}
          </button>
        ) : changeDateHref ? (
          <a href={changeDateHref} target="_blank" rel="noreferrer" className={changeDateClass} style={changeDateStyle}>
            {changeDateInner}
          </a>
        ) : null}

        <p className="mb-2 mt-5 font-sans text-[12px] font-semibold text-[#5a5d77]">
          {t("activity.cancel_reasons_label", { defaultValue: "Or tell us why (optional)" })}
        </p>
        <div className="flex flex-wrap gap-2" role="group">
          {REASONS.map((r) => {
            const on = reason === r.key;
            return (
              <button
                key={r.key}
                type="button"
                aria-pressed={on}
                onClick={() => setReason(on ? null : r.key)}
                className="inline-flex h-9 items-center rounded-full px-3.5 font-sans text-[13px] font-semibold transition-transform duration-100 active:scale-[0.97]"
                style={on ? CHIP_ON : CHIP_REST}
              >
                {t(`activity.cancel_reason_${r.key}`)}
              </button>
            );
          })}
        </div>
        {reason === "other" ? (
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            maxLength={400}
            aria-label={t("activity.cancel_reason_label", { defaultValue: "Reason (optional)" })}
            placeholder={t("activity.cancel_reason_other_placeholder", { defaultValue: "In a few words" })}
            className="mt-2.5 w-full rounded-2xl px-3 py-2 font-sans text-[13.5px] text-[#110E39] outline-none"
            style={pewterInput}
          />
        ) : null}

        {errorText && (
          <p role="alert" className="mt-3 rounded-xl px-3 py-2 font-sans text-[12.5px] font-semibold" style={pewterErrorNote}>
            {errorText}
          </p>
        )}

        <button
          type="button"
          disabled={mutation.isPending}
          onClick={() => mutation.mutate()}
          className="mt-4 flex h-11 w-full items-center justify-center gap-1.5 rounded-[14px] font-sans text-[14px] font-bold disabled:pointer-events-none disabled:opacity-50"
          style={{ color: PW.red }}
        >
          {mutation.isPending ? <Icon name="ri-loader-4-line animate-spin" className="text-[15px]" aria-hidden /> : null}
          {t("activity.confirm_cancel", { defaultValue: "Cancel booking" })}
        </button>
        <p className="mt-1 text-balance text-center font-sans text-[12px] font-medium leading-snug text-[#5a5d77]">
          {payByLink
            ? t("activity.cancel_booking_description_link")
            : t("activity.cancel_booking_description", {
                defaultValue:
                  "Cancelling will release your reservation and notify our team. You won't owe anything since payment is on arrival.",
              })}
        </p>
      </div>
    </div>
  );
}
